import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Edit2, Trash2, Check, X, Eye, Sparkles, 
  Clock, RefreshCw, Settings, Loader2, ChevronDown, ChevronUp,
  Calendar, Zap, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'product_roundup', name: 'Product Roundup' },
  { id: 'how_to_guide', name: 'How-To Guide' },
  { id: 'creator_spotlight', name: 'Creator Spotlight' },
  { id: 'industry_trends', name: 'Industry Trends' },
  { id: 'success_stories', name: 'Success Stories' },
  { id: 'tool_comparison', name: 'Tool Comparison' },
  { id: 'beginner_guide', name: 'Beginner Guide' },
  { id: 'advanced_tips', name: 'Advanced Tips' },
  { id: 'market_analysis', name: 'Market Analysis' },
  { id: 'case_study', name: 'Case Study' },
];

function ArticleAdmin() {
  const [articles, setArticles] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, published: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [runningCron, setRunningCron] = useState(false);
  const [automationSettings, setAutomationSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);

  // Fetch articles
  const fetchArticles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/articles`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filter !== 'all' ? { status: filter } : {}
      });
      setArticles(response.data.articles || []);
      setCounts(response.data.counts || { draft: 0, published: 0, rejected: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  // Fetch automation settings
  const fetchAutomationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/articles/automation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch automation settings');
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchAutomationSettings();
  }, [filter]);

  // Publish article
  const handlePublish = async (articleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/articles/${articleId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article published!');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to publish article');
    }
  };

  // Reject article
  const handleReject = async (articleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/articles/${articleId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article rejected');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to reject article');
    }
  };

  // Delete article
  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // Generate new AI article
  const handleGenerateArticle = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/articles/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI article generated! Review it in drafts.');
      fetchArticles();
      fetchAutomationSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate article');
    } finally {
      setGenerating(false);
    }
  };

  // Run the full Mon/Wed/Fri cron immediately (article + YouTube auto-publish).
  // The backend kicks off the LLM work in the background and returns 202 + job_id,
  // then we poll cron-job/{job_id} until complete (avoids ingress 60s timeout).
  const handleRunCronNow = async () => {
    if (!window.confirm("Run the full blog cron now? This generates a new article AND auto-publishes a YouTube video derived from it — same as the scheduled Mon/Wed/Fri 6 AM PT job. Continue?")) {
      return;
    }
    try {
      setRunningCron(true);
      const token = localStorage.getItem('token');
      const startRes = await axios.post(`${API}/api/admin/articles/run-cron-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        toast.error('Cron started but no job_id returned');
        setRunningCron(false);
        return;
      }
      toast.info('Cron queued — generating article + YouTube content (30-90s)...');

      // Poll every 5s for up to ~3 minutes. Status transitions:
      // queued → running → complete (or failed).
      const maxAttempts = 36;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const pollRes = await axios.get(`${API}/api/admin/articles/cron-job/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = pollRes.data || {};
          if (data.status === 'complete') {
            const ytBit = data.youtube_status === 'published'
              ? '+ YouTube video published'
              : data.youtube_status === 'failed'
                ? `(YouTube failed: ${data.youtube_error || 'unknown'})`
                : '';
            toast.success(`Cron complete: "${data.article_title || 'article'}" ${ytBit}`);
            fetchArticles();
            fetchAutomationSettings();
            setRunningCron(false);
            return;
          }
          if (data.status === 'failed') {
            toast.error(`Cron failed: ${data.error || 'unknown'}`);
            setRunningCron(false);
            return;
          }
        } catch (pollErr) {
          // Continue polling on transient errors
          console.warn('Cron poll failed (will retry):', pollErr);
        }
      }
      toast.warning('Cron is still running. Refresh the page in a minute to see the new article.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cron start failed');
    } finally {
      setRunningCron(false);
    }
  };

  // Scan for duplicates (dry run), then confirm to move extras to draft.
  const handleDedupe = async () => {
    try {
      setDeduping(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const scan = await axios.post(`${API}/api/admin/articles/dedupe`, {}, { headers, params: { dry_run: true } });
      const found = scan.data.duplicates_found;
      if (!found) {
        toast.success(`No duplicates found across ${scan.data.published_scanned} published articles`);
        setDedupeResult(scan.data);
        return;
      }
      if (!window.confirm(`Found ${found} duplicate article(s). Move them to draft (hidden, not deleted)? The oldest copy of each stays published.`)) {
        setDedupeResult(scan.data);
        return;
      }
      const res = await axios.post(`${API}/api/admin/articles/dedupe`, {}, { headers, params: { dry_run: false } });
      setDedupeResult(res.data);
      toast.success(`Moved ${res.data.moved_to_draft} duplicate article(s) to draft`);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Duplicate cleanup failed');
    } finally {
      setDeduping(false);
    }
  };

  // Toggle automation
  const handleToggleAutomation = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/articles/automation`, {
        enabled: !automationSettings?.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(automationSettings?.enabled ? 'Automation paused' : 'Automation enabled');
      fetchAutomationSettings();
    } catch (error) {
      toast.error('Failed to update automation settings');
    }
  };

  // Update article
  const handleUpdateArticle = async (articleId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/articles/${articleId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article updated');
      setEditingArticle(null);
      fetchArticles();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-neutral-900/50 rounded-2xl p-6 border border-white/10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-white" />
          <h2 className="text-xl font-bold">Article Management</h2>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleGenerateArticle}
            disabled={generating || runningCron}
            className="bg-gradient-to-r from-[#00F0FF] to-white hover:opacity-90"
            data-testid="generate-article-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate AI Article
          </Button>

          <Button
            onClick={handleRunCronNow}
            disabled={runningCron || generating}
            variant="outline"
            className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
            data-testid="run-cron-now-btn"
            title="Run the full Mon/Wed/Fri cron now (article + YouTube auto-publish)"
          >
            {runningCron ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Run cron now
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setShowSettings(!showSettings)}
            className="border-white/20"
            data-testid="automation-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Automation
          </Button>

          <Button
            variant="outline"
            onClick={handleDedupe}
            disabled={deduping}
            className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
            data-testid="dedupe-articles-btn"
          >
            {deduping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
            Clean up duplicates
          </Button>
        </div>
      </div>

      {dedupeResult && (
        <div className="mb-6 p-4 bg-amber-500/5 rounded-xl border border-amber-400/20" data-testid="dedupe-result-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-amber-200" data-testid="dedupe-result-summary">
              Scanned {dedupeResult.published_scanned} published · {dedupeResult.duplicates_found} duplicate(s) found
              {dedupeResult.dry_run ? ' · no changes made' : ` · ${dedupeResult.moved_to_draft} moved to draft`}
            </p>
            <button onClick={() => setDedupeResult(null)} className="text-neutral-400 hover:text-white" data-testid="dedupe-result-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {dedupeResult.duplicates.map((d) => (
              <li key={d.article_id} className="text-xs text-neutral-400" data-testid={`dedupe-item-${d.article_id}`}>
                <span className="text-white">{d.title}</span> ({formatDate(d.created_at)}) — copy of "{d.duplicate_of_title}"
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Automation Settings Panel */}
      {showSettings && automationSettings && (
        <div className="mb-6 p-4 bg-neutral-800/50 rounded-xl border border-white/10">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#00F0FF]" />
            Weekly Automation
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-neutral-900/50 rounded-lg">
              <span className="text-sm text-neutral-400">Status</span>
              <Button
                size="sm"
                variant={automationSettings.enabled ? "default" : "outline"}
                onClick={handleToggleAutomation}
                className={automationSettings.enabled ? "bg-green-600 hover:bg-green-700" : ""}
              >
                {automationSettings.enabled ? 'Enabled' : 'Paused'}
              </Button>
            </div>
            <div className="p-3 bg-neutral-900/50 rounded-lg">
              <span className="text-sm text-neutral-400 block mb-1">Schedule</span>
              <span className="text-white">Every {automationSettings.day_of_week || 'Monday'} at {automationSettings.hour || 9}:00 UTC</span>
            </div>
            <div className="p-3 bg-neutral-900/50 rounded-lg">
              <span className="text-sm text-neutral-400 block mb-1">Total Generated</span>
              <span className="text-white text-lg font-bold">{automationSettings.total_generated || 0}</span>
            </div>
          </div>
          {automationSettings.last_generated && (
            <p className="text-xs text-neutral-500 mt-3">
              Last generated: {formatDate(automationSettings.last_generated)}
            </p>
          )}
        </div>
      )}

      {/* Stats & Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filter === 'all' ? 'bg-[#00F0FF] text-black' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
          }`}
        >
          All ({counts.total})
        </button>
        <button
          onClick={() => setFilter('draft')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filter === 'draft' ? 'bg-yellow-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
          }`}
        >
          Drafts ({counts.draft})
        </button>
        <button
          onClick={() => setFilter('published')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filter === 'published' ? 'bg-green-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
          }`}
        >
          Published ({counts.published})
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filter === 'rejected' ? 'bg-red-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
          }`}
        >
          Rejected ({counts.rejected})
        </button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchArticles}
          className="ml-auto"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Articles List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 text-neutral-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No articles found</p>
          <p className="text-sm mt-2">Click "Generate AI Article" to create your first automated article</p>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article) => (
            <div 
              key={article.article_id}
              className="bg-neutral-800/50 rounded-xl border border-white/10 overflow-hidden"
            >
              {/* Article Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-neutral-700/30 transition-colors"
                onClick={() => setExpandedArticle(expandedArticle === article.article_id ? null : article.article_id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        article.status === 'published' ? 'bg-green-600/20 text-green-400' :
                        article.status === 'draft' ? 'bg-yellow-600/20 text-yellow-400' :
                        'bg-red-600/20 text-red-400'
                      }`}>
                        {article.status}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF]/20 text-[#00F0FF]">
                        {article.category_name}
                      </span>
                      {article.ai_generated && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF]/20 text-[#00F0FF] flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> AI
                        </span>
                      )}
                      {article.featured && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF]/20 text-[#00F0FF]">
                          Featured
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-white truncate">{article.title}</h3>
                    <p className="text-sm text-neutral-400 mt-1 line-clamp-1">{article.excerpt}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {expandedArticle === article.article_id ? (
                      <ChevronUp className="w-5 h-5 text-neutral-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-neutral-400" />
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(article.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.read_time}
                  </span>
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedArticle === article.article_id && (
                <div className="border-t border-white/10 p-4 bg-neutral-900/30">
                  {/* Preview */}
                  <div className="mb-4 p-4 bg-neutral-800/50 rounded-lg max-h-60 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-neutral-300 mb-2">Content Preview</h4>
                    <div className="text-sm text-neutral-400 whitespace-pre-line">
                      {article.content.substring(0, 500)}...
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {article.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handlePublish(article.article_id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`publish-btn-${article.article_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Publish
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(article.article_id)}
                          className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                          data-testid={`reject-btn-${article.article_id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    
                    {article.status === 'published' && (
                      <a
                        href={`/resources`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 bg-[#00F0FF]/20 text-[#00F0FF] rounded-lg text-sm hover:bg-[#00F0FF]/30"
                      >
                        <Eye className="w-4 h-4" /> View Live
                      </a>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingArticle(article)}
                      className="border-white/20"
                    >
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(article.article_id)}
                      className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingArticle && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Edit Article</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingArticle(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Title</label>
                <Input
                  value={editingArticle.title}
                  onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
                  className="bg-neutral-800 border-white/10"
                />
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Excerpt</label>
                <textarea
                  value={editingArticle.excerpt}
                  onChange={(e) => setEditingArticle({...editingArticle, excerpt: e.target.value})}
                  className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-neutral-400 mb-1 block">Category</label>
                  <select
                    value={editingArticle.category}
                    onChange={(e) => {
                      const cat = CATEGORIES.find(c => c.id === e.target.value);
                      setEditingArticle({
                        ...editingArticle, 
                        category: e.target.value,
                        category_name: cat?.name || e.target.value
                      });
                    }}
                    className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm text-neutral-400 mb-1 block">Featured</label>
                  <select
                    value={editingArticle.featured ? 'yes' : 'no'}
                    onChange={(e) => setEditingArticle({...editingArticle, featured: e.target.value === 'yes'})}
                    className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes - Show in Featured</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Content (Markdown)</label>
                <textarea
                  value={editingArticle.content}
                  onChange={(e) => setEditingArticle({...editingArticle, content: e.target.value})}
                  className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white resize-none font-mono text-sm"
                  rows={12}
                />
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Image URL</label>
                <Input
                  value={editingArticle.image_url || ''}
                  onChange={(e) => setEditingArticle({...editingArticle, image_url: e.target.value})}
                  className="bg-neutral-800 border-white/10"
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditingArticle(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleUpdateArticle(editingArticle.article_id, {
                    title: editingArticle.title,
                    excerpt: editingArticle.excerpt,
                    content: editingArticle.content,
                    category: editingArticle.category,
                    category_name: editingArticle.category_name,
                    featured: editingArticle.featured,
                    image_url: editingArticle.image_url
                  })}
                  className="bg-white text-black hover:bg-[#00F0FF]/90"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleAdmin;
